import { useCallback, useState } from 'react';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type { UpdateSubmissionBody, UpdateSubmissionResponse } from '../types/submission';

interface UseUpdateSubmissionReturn {
  update: (body: UpdateSubmissionBody) => Promise<UpdateSubmissionResponse>;
  saving: boolean;
  error: ReviewError | null;
  reset: () => void;
}

/**
 * PATCH mutation hook. Returns a `saving` flag and the last error
 * (normalised via `toReviewError`) so the dialog can render
 * per-error messaging inline. Callers `await update(body)` and
 * handle success / STALE_RECORD themselves — the hook re-throws so
 * the promise chain in the dialog sees the failure.
 */
export function useUpdateSubmission(
  resource: string | undefined,
  id: number | string | undefined,
): UseUpdateSubmissionReturn {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ReviewError | null>(null);

  const update = useCallback(
    async (body: UpdateSubmissionBody): Promise<UpdateSubmissionResponse> => {
      if (!resource || id === undefined || id === null || id === '') {
        throw new Error('resource and id are required to update a submission');
      }
      setSaving(true);
      setError(null);
      try {
        return await reviewService.updateSubmission(resource, id, body);
      } catch (err) {
        const mapped = toReviewError(err);
        setError(mapped);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [resource, id],
  );

  const reset = useCallback(() => setError(null), []);

  return { update, saving, error, reset };
}
